import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import MainLayout from '../../components/MainLayout';
import { useTranslation } from '../../context/LanguageContext';
import StepNav from './StepNav';
import {
  featureShotList,
  defaultFeatureShotId,
  processTimeseries,
  processFeatures,
  visionFeatures,
  visionROIs,
  thermalGrid,
  thermalColorbar,
  thermalStats,
  thermalFeatures,
  registration,
  getProduct,
} from '../../mock/moduleb';
import './moduleb-common.css';
import './ModuleBFeatures.css';

// 온도값 → 색상 (파랑 25℃ → 빨강 120℃)
function tempToColor(t, min, max) {
  const ratio = Math.max(0, Math.min(1, (t - min) / (max - min)));
  // 파랑(220) → 청록 → 노랑 → 빨강(0) HSL 보간
  const hue = 220 - ratio * 220;
  return `hsl(${hue}, 85%, ${30 + ratio * 25}%)`;
}

function FeatureList({ items }) {
  const { t } = useTranslation();
  return (
    <div className="mb-kv-list">
      {items.map((f) => (
        <div key={f.labelKey} className="mb-kv-row">
          <span className="mb-kv-key">{t(f.labelKey)}</span>
          <span className="mb-kv-val">
            {f.value}
            {(f.unit || f.unitKey) && <span className="mb-kv-unit">{f.unitKey ? t(f.unitKey) : f.unit}</span>}
          </span>
        </div>
      ))}
    </div>
  );
}

function ModuleBFeatures() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const shotFromUrl = searchParams.get('shot');
  const [selectedShot, setSelectedShot] = useState(shotFromUrl || defaultFeatureShotId);
  const product = getProduct(selectedShot);

  return (
    <MainLayout>
      <div className="mb-container">
        <StepNav current={2} shotId={selectedShot} />

        <div className="mb-page-header">
          <div>
            <div className="mb-page-title">
              <span className="mb-step-tag">STEP 2</span>{t('pages.moduleb.nav.steps.features.title')}
            </div>
            <div className="mb-page-subtitle">
              {t('pages.moduleb.features.subtitle')}
            </div>
          </div>
          <div className="mb-page-meta">
            {t('pages.moduleb.features.meta')} <strong>{selectedShot}</strong> · <strong>{product.productId}</strong>
          </div>
        </div>

        <div className="mb-body">
          <div className="feat-layout">
            {/* 좌측 샷 리스트 */}
            <div className="mb-card feat-shotlist">
              <div className="mb-card-title">
                <span className="material-symbols-outlined">list</span>{t('pages.moduleb.features.selectShot')}
              </div>
              {featureShotList.map((s) => (
                <button
                  key={s.shotId}
                  type="button"
                  className={`feat-shot-item ${selectedShot === s.shotId ? 'active' : ''}`}
                  onClick={() => setSelectedShot(s.shotId)}
                >
                  <span className="feat-shot-ids">
                    <span className="mb-mono">{s.shotId}</span>
                    <span className="feat-shot-product">{s.productId}</span>
                  </span>
                  <span className="feat-shot-dot" />
                </button>
              ))}
            </div>

            {/* 3분할 모달리티 패널 */}
            <div className="feat-panels">
              {/* 시계열 (공정) */}
              <div className="mb-card feat-panel">
                <div className="mb-card-title">
                  <span className="material-symbols-outlined" style={{ color: '#3B82F6' }}>show_chart</span>
                  {t('pages.moduleb.features.panelProcess')}
                </div>
                <div className="feat-chart">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={processTimeseries} margin={{ top: 6, right: 8, left: -18, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#2a2f38" />
                      <XAxis dataKey="t" stroke="#6b7280" tick={{ fontSize: 10 }} tickFormatter={(v) => `${v}`} />
                      <YAxis stroke="#6b7280" tick={{ fontSize: 10 }} />
                      <Tooltip
                        contentStyle={{ background: '#1a1f28', border: '1px solid #3a3f47', borderRadius: 6, fontSize: 12 }}
                        labelStyle={{ color: '#9CA3AF' }}
                        labelFormatter={(v) => `${v} ms`}
                      />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                      <Line type="monotone" dataKey="pressure" name={t('pages.moduleb.features.legend.pressure')} stroke="#3B82F6" dot={false} strokeWidth={2} />
                      <Line type="monotone" dataKey="velocity" name={t('pages.moduleb.features.legend.velocity')} stroke="#22c55e" dot={false} strokeWidth={2} />
                      <Line type="monotone" dataKey="holding" name={t('pages.moduleb.features.legend.holding')} stroke="#f97316" dot={false} strokeWidth={2} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
                <div className="mb-section-label">{t('pages.moduleb.features.extracted')}</div>
                <FeatureList items={processFeatures} />
              </div>

              {/* 형상 (비전) */}
              <div className="mb-card feat-panel">
                <div className="mb-card-title">
                  <span className="material-symbols-outlined" style={{ color: '#22c55e' }}>image_search</span>
                  {t('pages.moduleb.features.panelVision')}
                </div>
                <div className="feat-vision-canvas">
                  <div className="feat-vision-placeholder">
                    <span className="material-symbols-outlined">photo_camera</span>
                    {t('pages.moduleb.features.imagePlaceholder')}
                  </div>
                  {/* ROI 오버레이 */}
                  {visionROIs.map((roi) => (
                    <div
                      key={roi.id}
                      className={`feat-roi ${roi.id === 'roi-2' ? 'candidate' : ''}`}
                      style={{ left: `${roi.x}%`, top: `${roi.y}%`, width: `${roi.w}%`, height: `${roi.h}%` }}
                    >
                      <span className="feat-roi-label">{t(roi.labelKey)}</span>
                    </div>
                  ))}
                  {/* 윤곽선 흉내 */}
                  <svg className="feat-contour" viewBox="0 0 100 100" preserveAspectRatio="none">
                    <path d="M22,30 Q40,12 60,26 T86,44 Q78,72 52,78 T20,60 Q14,44 22,30 Z"
                      fill="none" stroke="#22c55e" strokeWidth="0.6" strokeDasharray="2 1.5" opacity="0.8" />
                  </svg>
                </div>
                <div className="mb-section-label">{t('pages.moduleb.features.extracted')}</div>
                <FeatureList items={visionFeatures} />
              </div>

              {/* 온도장 (열화상) */}
              <div className="mb-card feat-panel">
                <div className="mb-card-title">
                  <span className="material-symbols-outlined" style={{ color: '#f97316' }}>thermostat</span>
                  {t('pages.moduleb.features.panelThermal')}
                  <span className="mb-card-badge">{t('pages.moduleb.common.newBadge')}</span>
                </div>
                <div className="feat-thermal">
                  <div className="feat-heatmap">
                    {thermalGrid.map((row, r) => (
                      <div key={r} className="feat-heatmap-row">
                        {row.map((temp, c) => {
                          const isHot = r === thermalStats.hotspot.row && c === thermalStats.hotspot.col;
                          return (
                            <div
                              key={c}
                              className={`feat-heatmap-cell ${isHot ? 'hotspot' : ''}`}
                              style={{ background: tempToColor(temp, thermalColorbar.min, thermalColorbar.max) }}
                              title={`${temp}${thermalColorbar.unit}`}
                            >
                              {isHot && <span className="material-symbols-outlined">location_on</span>}
                            </div>
                          );
                        })}
                      </div>
                    ))}
                  </div>
                  {/* 컬러바 */}
                  <div className="feat-colorbar">
                    <div className="feat-colorbar-scale" />
                    <div className="feat-colorbar-ticks">
                      <span>{thermalColorbar.max}℃</span>
                      <span>{Math.round((thermalColorbar.max + thermalColorbar.min) / 2)}℃</span>
                      <span>{thermalColorbar.min}℃</span>
                    </div>
                  </div>
                </div>
                <div className="mb-section-label">{t('pages.moduleb.features.extracted')}</div>
                <FeatureList items={thermalFeatures} />
              </div>
            </div>
          </div>

          {/* 정합 시각화 */}
          <div className="mb-card mb-mt">
            <div className="mb-card-title">
              <span className="material-symbols-outlined">grid_on</span>
              {t('pages.moduleb.features.registrationTitle')}
            </div>
            <div className="feat-reg">
              <div className="feat-reg-canvas">
                {/* 격자 */}
                <svg viewBox="0 0 100 100" className="feat-reg-grid" preserveAspectRatio="none">
                  {Array.from({ length: registration.gridSize + 1 }).map((_, i) => (
                    <React.Fragment key={i}>
                      <line x1={(i / registration.gridSize) * 100} y1="0" x2={(i / registration.gridSize) * 100} y2="100" stroke="#2a2f38" strokeWidth="0.3" />
                      <line x1="0" y1={(i / registration.gridSize) * 100} x2="100" y2={(i / registration.gridSize) * 100} stroke="#2a2f38" strokeWidth="0.3" />
                    </React.Fragment>
                  ))}
                </svg>
                {/* 비전 ROI (초록) */}
                <div className="feat-reg-layer vision" style={{ left: '22%', top: '26%', width: '40%', height: '32%' }}>
                  <span>{t('pages.moduleb.features.visionRoi')}</span>
                </div>
                {/* 온도장 핫스팟 (주황) */}
                <div className="feat-reg-layer thermal" style={{ left: '58%', top: '18%', width: '30%', height: '30%' }}>
                  <span>{t('pages.moduleb.features.thermalHotspot')}</span>
                </div>
              </div>
              <div className="feat-reg-info">
                <div className="feat-reg-stat">
                  <div className="feat-reg-stat-label">{t('pages.moduleb.features.regAccuracy')}</div>
                  <div className="feat-reg-stat-value" style={{ color: '#22c55e' }}>{registration.accuracy}<span>%</span></div>
                </div>
                <div className="feat-reg-stat">
                  <div className="feat-reg-stat-label">{t('pages.moduleb.features.offsetX')}</div>
                  <div className="feat-reg-stat-value">{registration.offsetPx.x}<span>px</span></div>
                </div>
                <div className="feat-reg-stat">
                  <div className="feat-reg-stat-label">{t('pages.moduleb.features.offsetY')}</div>
                  <div className="feat-reg-stat-value">{registration.offsetPx.y}<span>px</span></div>
                </div>
                <div className="feat-reg-legend">
                  <div><span className="feat-reg-chip vision" /> {t('pages.moduleb.features.legendVision')}</div>
                  <div><span className="feat-reg-chip thermal" /> {t('pages.moduleb.features.legendThermal')}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </MainLayout>
  );
}

export default ModuleBFeatures;
